import { useState } from "react";
import SEOHead from "@/components/feature/SEOHead";

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError("");

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get("company_alt") as string;
    if (honeypot && honeypot.trim() !== "") {
      setFormStatus("success");
      setSubmitting(false);
      return;
    }

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== "company_alt" && value) {
          body.append(key, value as string);
        }
      });

      const response = await fetch("https://readdy.ai/api/form/d9phmdhn2trhb35gvflg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === "OK") {
        setFormStatus("success");
        form.reset();
      } else {
        const msg =
          parsed?.meta?.message ||
          parsed?.message ||
          parsed?.meta?.detail ||
          responseText ||
          "提交失败，请稍后重试";
        if (msg.toLowerCase().includes("spam") || msg.includes("spam")) {
          setFormError("提交失败，请稍后重试");
        } else {
          setFormError(msg);
        }
        setFormStatus("error");
      }
    } catch {
      setFormError("网络错误，请检查网络后重试");
      setFormStatus("error");
    } finally {
      setSubmitting(false);
    }
  };

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "AboutPage",
        "@id": "https://17cyqc.com/about/#about",
        url: "https://17cyqc.com/about",
        name: "关于我们 - 17c一起草国",
        description: "了解17c一起草国的品牌故事、用户口碑和联系方式。我们致力于打造优质内容分享平台。",
        isPartOf: { "@id": "https://17cyqc.com/#website" },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://17cyqc.com" },
          { "@type": "ListItem", position: 2, name: "关于我们" },
        ],
      },
    ],
  };

  return (
    <>
      <SEOHead
        meta={{
          title: "关于我们 - 17c一起草国",
          description: "了解17c一起草国的品牌故事、平台理念和用户口碑。我们致力于打造高质量内容分享平台，为读者提供免费完整版阅读体验。联系我们获取更多信息。",
          keywords: "17c一起草国, 关于我们, 品牌介绍, 联系方式, 内容平台",
          canonical: "https://17cyqc.com/about",
          ogTitle: "关于我们 - 17c一起草国",
          ogDescription: "了解17c一起草国的品牌故事、平台理念和用户口碑。我们致力于打造高质量内容分享平台。",
          ogUrl: "https://17cyqc.com/about",
          jsonLd,
        }}
      />

      <section className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
        <div className="text-center mb-12">
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-3">
            关于我们
          </h1>
          <p className="text-sm text-foreground-600 max-w-2xl mx-auto">
            了解17c一起草国背后的故事
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-16">
          <div>
            <h2 className="text-xl font-bold text-foreground-950 mb-4">品牌介绍</h2>
            <div className="space-y-4 text-sm text-foreground-700 leading-relaxed">
              <p>
                17c一起草国成立于2023年，是一个致力于为中文读者提供高品质内容阅读体验的平台。我们的名字寓意着<strong>一起探索知识的草原</strong>，在信息的海洋中找到属于你的那片绿洲。
              </p>
              <p>
                平台创立之初，我们便确立了"<strong>内容为王，读者至上</strong>"的核心理念。在信息碎片化日益严重的今天，17c一起草国坚持选择深度、有料、有温度的内容，为每一位求知者打造专属的阅读空间。
              </p>
              <p>
                我们的编辑团队由来自不同领域的资深内容创作者组成，涵盖科技、人文、教育、商业等多个方向。每位编辑都秉持着对文字的热忱和对品质的执着，确保平台上每一篇文章都值得你的时间。
              </p>
              <p>
                截至目前，17c一起草国已累计发布<strong>超过5000篇</strong>原创和精选内容，服务超过<strong>50万</strong>活跃读者，日均阅读量突破<strong>10万次</strong>。这些数字背后，是我们对内容品质始终如一的追求。
              </p>
            </div>

            <h2 className="text-xl font-bold text-foreground-950 mt-10 mb-4">我们的理念</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { icon: "ri-star-line", title: "品质第一", desc: "每篇文章都经过严格筛选和编辑审核" },
                { icon: "ri-user-heart-line", title: "读者至上", desc: "以读者需求为导向，持续优化阅读体验" },
                { icon: "ri-lightbulb-flash-line", title: "创新驱动", desc: "不断探索内容创作和呈现的新方式" },
                { icon: "ri-shake-hands-line", title: "开放合作", desc: "欢迎优秀创作者加入，共建优质内容生态" },
              ].map((item) => (
                <div key={item.title} className="bg-background-100 rounded-lg p-5">
                  <div className="w-8 h-8 flex items-center justify-center rounded-md bg-accent-100 text-accent-500 mb-3">
                    <i className={item.icon} />
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-950 mb-1">{item.title}</h3>
                  <p className="text-xs text-foreground-600">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h2 className="text-xl font-bold text-foreground-950 mb-4">用户口碑</h2>
            <div className="space-y-4">
              {[
                {
                  name: "张先生",
                  role: "互联网从业者",
                  text: "17c一起草国是我每天必看的内容平台。文章质量很高，观点独到，帮我节省了大量筛选信息的时间。深度解读板块是我最喜欢的，每篇都值得反复阅读。",
                  rating: 5,
                },
                {
                  name: "李女士",
                  role: "自由撰稿人",
                  text: "作为一个写作者，我非常欣赏17c一起草国对内容品质的坚持。平台上的文章不仅信息量大，而且文笔流畅，常常给我很多创作灵感。也感谢平台给原创作者提供了很好的展示机会。",
                  rating: 5,
                },
                {
                  name: "王同学",
                  role: "大学生",
                  text: "教程指南版块对我的学习帮助非常大！很多实用的学习方法和效率技巧都是从17c一起草国学到的。推荐给所有想在大学期间提升自己的同学。",
                  rating: 5,
                },
                {
                  name: "赵总",
                  role: "创业者",
                  text: "在碎片化时代，能找到像17c一起草国这样专注于深度内容的平台实属不易。商业分析和趋势洞察类的文章帮我把握了不少行业动向，强烈推荐。",
                  rating: 5,
                },
              ].map((review, idx) => (
                <div key={idx} className="bg-background-100 rounded-lg p-5">
                  <div className="flex items-center gap-1 mb-2">
                    {Array.from({ length: review.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-amber-500 text-sm" />
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-3">
                    &ldquo;{review.text}&rdquo;
                  </p>
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-accent-100 flex items-center justify-center text-accent-600 text-xs font-bold">
                      {review.name.slice(0, 1)}
                    </div>
                    <div>
                      <p className="text-xs font-medium text-foreground-950">{review.name}</p>
                      <p className="text-xs text-foreground-500">{review.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="border-t border-background-200/70 pt-12">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-xl font-bold text-foreground-950 mb-3">联系我们</h2>
              <p className="text-sm text-foreground-600">
                有任何问题、建议或合作意向，欢迎通过以下方式与我们取得联系
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
              {[
                { icon: "ri-mail-line", label: "电子邮箱", value: "contact@17cyqc.com" },
                { icon: "ri-phone-line", label: "客服电话", value: "400-888-0000" },
                { icon: "ri-map-pin-line", label: "公司地址", value: "北京市朝阳区建国路88号" },
              ].map((item) => (
                <div key={item.label} className="bg-background-100 rounded-lg p-5 text-center">
                  <div className="w-9 h-9 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-500 mb-3">
                    <i className={item.icon} />
                  </div>
                  <p className="text-xs text-foreground-500 mb-1">{item.label}</p>
                  <p className="text-sm font-medium text-foreground-950">{item.value}</p>
                </div>
              ))}
            </div>

            <form
              onSubmit={handleSubmit}
              data-readdy-form=""
              className="bg-background-100 rounded-lg p-6"
            >
              {formStatus === "success" && (
                <div className="mb-4 p-3 rounded-md bg-green-50 text-green-700 text-sm">
                  感谢您的留言！我们会尽快回复您。
                </div>
              )}
              {formStatus === "error" && formError && (
                <div className="mb-4 p-3 rounded-md bg-red-50 text-red-700 text-sm">
                  {formError}
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="name" className="block text-xs font-medium text-foreground-700 mb-1.5">
                    姓名 <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    className="w-full px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-accent-300 transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-xs font-medium text-foreground-700 mb-1.5">
                    邮箱 <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    className="w-full px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-accent-300 transition-colors"
                    placeholder="请输入您的邮箱"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label htmlFor="subject" className="block text-xs font-medium text-foreground-700 mb-1.5">
                  主题
                </label>
                <input
                  id="subject"
                  name="subject"
                  type="text"
                  className="w-full px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-accent-300 transition-colors"
                  placeholder="请输入留言主题"
                />
              </div>

              <div className="mb-4">
                <label htmlFor="message" className="block text-xs font-medium text-foreground-700 mb-1.5">
                  留言内容 <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  maxLength={500}
                  rows={4}
                  className="w-full px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-accent-300 transition-colors resize-none"
                  placeholder="请输入您的留言内容（最多500字）"
                />
                <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
              </div>

              <div className="company-alt-wrapper mb-4">
                <input
                  name="company_alt"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="company-alt-input"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full py-2.5 bg-accent-500 text-background-50 rounded-md text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50 cursor-pointer whitespace-nowrap"
              >
                {submitting ? "提交中..." : "发送留言"}
              </button>
            </form>
          </div>
        </div>
      </section>
    </>
  );
}